import { FluentProvider } from "@fluentui/react-components"
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react"
import { DEFAULT_BRAND_COLOR, themesFromBrandColor } from "./brand"

export type ThemeMode = "light" | "dark"

const STORAGE_KEY = "rema.theme-mode"

interface ThemeContextValue {
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
  toggleMode: () => void
  brandColor: string
  // Permite re-tematizar en vivo (preview de branding en Ajustes)
  setBrandColor: (hex: string) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

// Claro por defecto (docs/05 §4); prefers-color-scheme solo se respeta la primera
// visita — en cuanto el usuario elige, su preferencia persistida manda.
const initialMode = (): ThemeMode => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === "light" || stored === "dark") return stored
  } catch {
    // localStorage puede no estar disponible (modo privado restrictivo)
  }
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

export interface ThemeProviderProps {
  children: ReactNode
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [mode, setModeState] = useState<ThemeMode>(initialMode)
  const [brandColor, setBrandColor] = useState(DEFAULT_BRAND_COLOR)

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // sin persistencia: el toggle sigue funcionando en la sesión
    }
  }, [])

  const toggleMode = useCallback(() => {
    setModeState((prev) => {
      const next = prev === "light" ? "dark" : "light"
      try {
        window.localStorage.setItem(STORAGE_KEY, next)
      } catch {
        // ídem: ignorar fallo de persistencia
      }
      return next
    })
  }, [])

  const themes = useMemo(() => themesFromBrandColor(brandColor), [brandColor])

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, setMode, toggleMode, brandColor, setBrandColor }),
    [mode, setMode, toggleMode, brandColor],
  )

  return (
    <ThemeContext.Provider value={value}>
      <FluentProvider
        theme={mode === "dark" ? themes.dark : themes.light}
        style={{ height: "100%" }}
      >
        {children}
      </FluentProvider>
    </ThemeContext.Provider>
  )
}

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error("useTheme debe usarse dentro de <ThemeProvider>")
  return ctx
}
